import { useParams, Link, Navigate } from "react-router-dom";
import { Check, X, Plus, Phone, ArrowRight, ArrowLeft, Leaf, ShieldCheck, Building2, Info } from "lucide-react";
import { Reveal, stagger, item } from "@/components/Reveal";
import { motion } from "framer-motion";
import { FAQ } from "@/components/FAQ";
import { RecurringPromise } from "@/components/RecurringPromise";
import { useLeadModal } from "@/context/LeadModalProvider";
import { usePricing } from "@/context/PricingProvider";
import { CONTACT, RESIDENTIAL, COMMERCIAL_CATEGORIES, COMMERCIAL_SERVICES, HIGH_TOUCH, IMAGES } from "@/data/site";
import { SERVICE_META, COMMERCIAL_PAGE } from "@/data/services_content";
import { LOCATIONS } from "@/data/locations";
import { useSEO, faqSchema, serviceSchema, breadcrumbSchema } from "@/lib/seo";

const RequestQuoteButton = ({ testId }) => {
  const { openProposal } = useLeadModal();
  return (
    <button onClick={openProposal} data-testid={testId}
      className="px-7 py-4 rounded-full bg-leaf text-white font-medium flex items-center justify-center gap-2 hover:scale-105 transition-transform">
      Request a Quote <ArrowRight className="w-4 h-4" />
    </button>
  );
};

const HeroButtons = ({ commercial }) => {
  const { openBooking, openQuote } = useLeadModal();
  if (commercial) return <div className="flex mt-8"><RequestQuoteButton testId="commercial-request-quote-btn" /></div>;
  return (
    <div className="flex flex-col sm:flex-row gap-3 mt-8">
      <button onClick={openQuote} data-testid="cta-quote-btn"
        className="group px-7 py-4 rounded-full bg-leaf text-white font-medium flex items-center justify-center gap-2 hover:scale-105 transition-transform">
        <Phone className="w-4 h-4" /> Get Instant Quote
      </button>
      <button onClick={openBooking} data-testid="cta-book-btn"
        className="px-7 py-4 rounded-full font-medium border border-navy/25 text-navy hover:bg-navy hover:text-white transition-all duration-200 hover:scale-105">
        Book Online
      </button>
    </div>
  );
};

const BookCtaBand = ({ heading }) => {
  const { openBooking, openQuote } = useLeadModal();
  return (
    <section className="py-16 md:py-24 bg-muted/40" data-testid="booking-cta">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <Leaf className="w-8 h-8 text-leaf mx-auto mb-4" />
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy mb-4">{heading}</h2>
        <p className="text-lg text-muted-foreground mb-8">Ready to schedule? Book online in a few taps — or get an instant price estimate first.</p>
        <div className="flex flex-col sm:flex-row justify-center gap-3">
          <button onClick={openQuote} data-testid="cta-quote-btn"
            className="group px-7 py-4 rounded-full bg-white border border-navy/25 text-navy font-medium flex items-center justify-center gap-2 hover:scale-105 transition-transform">
            <Phone className="w-4 h-4" /> Get Instant Quote
          </button>
          <button onClick={openBooking} data-testid="cta-book-btn"
            className="px-7 py-4 rounded-full font-medium bg-leaf text-white hover:scale-105 transition-transform duration-200">
            Book Online
          </button>
        </div>
      </div>
    </section>
  );
};

const OtherLinks = ({ currentSlug }) => (
  <section className="py-16 md:py-20 border-t border-border">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-navy mb-6">Explore more of what we do</h2>
      </Reveal>
      <div className="flex flex-wrap gap-3 mb-8">
        {RESIDENTIAL.filter((s) => s.slug !== currentSlug).map((s) => (
          <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`link-service-${s.slug}`}
            className="px-4 py-2 rounded-full text-sm font-medium bg-white border border-border text-navy hover:border-leaf transition-colors">{s.name}</Link>
        ))}
        {currentSlug !== "commercial-cleaning" && (
          <Link to="/services/commercial-cleaning" className="px-4 py-2 rounded-full text-sm font-medium bg-white border border-border text-navy hover:border-leaf transition-colors">Commercial Cleaning</Link>
        )}
      </div>
      <Reveal>
        <h3 className="font-heading text-lg font-semibold text-navy mb-4">Areas we serve across the DMV</h3>
      </Reveal>
      <div className="flex flex-wrap gap-2">
        {LOCATIONS.map((l) => (
          <Link key={l.slug} to={`/service-areas/${l.slug}`} data-testid={`link-area-${l.slug}`}
            className="px-4 py-2 rounded-full text-sm bg-muted text-navy hover:bg-leaf hover:text-white transition-colors">{l.name}, {l.state}</Link>
        ))}
      </div>
    </div>
  </section>
);

function ListCard({ title, icon: Icon, color, items }) {
  const ring = { leaf: "text-leaf bg-leaf/10", navy: "text-navy bg-navy/10", blush: "text-blush bg-blush/20" }[color];
  return (
    <div className="bg-white rounded-3xl p-6 border border-border h-full">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-4 ${ring}`}><Icon className="w-5 h-5" /></div>
      <h3 className="font-heading font-semibold text-navy mb-4">{title}</h3>
      <ul className="space-y-3">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2.5 text-sm text-muted-foreground leading-snug">
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color === "leaf" ? "text-leaf" : color === "navy" ? "text-navy/60" : "text-blush"}`} />{it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const pricing = usePricing();
  const isCommercial = slug === "commercial-cleaning";
  const res = RESIDENTIAL.find((s) => s.slug === slug);
  const meta = isCommercial ? COMMERCIAL_PAGE : SERVICE_META[slug];
  const valid = !!res || isCommercial;
  const name = isCommercial ? COMMERCIAL_PAGE.name : (res ? res.name : "");
  const faqs = meta ? meta.faqs : [];

  useSEO(meta ? {
    title: meta.title,
    description: meta.metaDescription,
    keywords: meta.keywords,
    path: `/services/${slug}`,
    jsonLd: [
      serviceSchema({ name, description: meta.metaDescription, path: `/services/${slug}` }),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name, path: `/services/${slug}` },
      ]),
      faqSchema(faqs),
    ],
  } : { title: "Cleaning Services | CJB Clean", path: "/services" });

  if (!valid) return <Navigate to="/services" replace />;

  const intro = meta.intro;

  return (
    <div data-testid="service-detail-page">
      {/* HERO */}
      <section className="pt-36 pb-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_82%_12%,rgba(95,165,68,0.10),transparent_45%)]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <Link to="/services" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-leaf transition-colors mb-6" data-testid="back-to-services">
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>
          <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">{isCommercial ? "Commercial" : "Residential"} · Green Cleaning</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-navy leading-[0.95] max-w-4xl">{meta.h1}</h1>
          <div className="mt-6 space-y-4 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            {intro.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <HeroButtons commercial={isCommercial} />
        </div>
      </section>

      {/* DETAIL */}
      {isCommercial ? (
        <section className="py-16 md:py-24 bg-muted/40" data-testid="commercial-detail">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="flex items-center gap-3 mb-6"><Building2 className="w-6 h-6 text-leaf" /><h2 className="font-heading text-2xl sm:text-3xl font-semibold text-navy">What we offer</h2></div>
            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
              {COMMERCIAL_SERVICES.map((c) => (
                <motion.div key={c} variants={item} className="flex items-center gap-3 bg-white rounded-2xl p-5 border border-border">
                  <span className="w-9 h-9 shrink-0 rounded-xl bg-leaf/10 flex items-center justify-center"><Check className="w-4 h-4 text-leaf" /></span>
                  <span className="font-medium text-navy text-sm">{c}</span>
                </motion.div>
              ))}
            </motion.div>
            <h3 className="font-heading text-xl font-semibold text-navy mb-5">Industries we serve</h3>
            <div className="grid md:grid-cols-2 gap-5">
              {COMMERCIAL_CATEGORIES.map((cat) => (
                <div key={cat.category} className="bg-white rounded-3xl p-7 border border-border">
                  <h4 className="font-heading font-semibold text-navy mb-4">{cat.category}</h4>
                  <ul className="space-y-2.5">
                    {cat.items.map((it) => <li key={it} className="flex items-start gap-2.5 text-muted-foreground"><Check className="w-4 h-4 mt-1 shrink-0 text-leaf" />{it}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="py-16 md:py-24 bg-muted/40" data-testid="residential-detail">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-4">
                <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
                  <img src={IMAGES.residential} alt={`${name} in Northern Virginia`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                  <div className="absolute bottom-0 p-6 text-white"><h2 className="font-heading text-2xl font-semibold">{name}</h2><p className="text-white/80 text-sm mt-1">{res.blurb}</p></div>
                </div>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-3 gap-5">
                <ListCard title="What's Included" icon={Check} color="leaf" items={res.included} />
                <ListCard title="Optional Add-Ons" icon={Plus} color="navy" items={res.addons} />
                <ListCard title="What's Not Included" icon={X} color="blush" items={res.excluded} />
              </div>
            </div>
            {res.prepNote && (
              <div className="mt-8 rounded-2xl border border-navy/15 bg-navy/5 p-5 flex items-start gap-3" data-testid="service-prep-note">
                <Info className="w-5 h-5 text-navy mt-0.5 shrink-0" />
                <p className="text-navy font-medium">{res.prepNote}</p>
              </div>
            )}
            <div className="mt-8 rounded-3xl border border-leaf/30 bg-leaf/5 p-8">
              <div className="flex items-center gap-3 mb-3"><ShieldCheck className="w-6 h-6 text-leaf" /><h3 className="font-heading text-xl font-semibold text-navy">High-Touch Surface Disinfection (Optional)</h3></div>
              <p className="text-muted-foreground mb-5 max-w-2xl">Add an extra layer of protection for the surfaces your household touches most.</p>
              <div className="flex flex-wrap gap-2">{HIGH_TOUCH.map((h) => <span key={h} className="text-sm px-4 py-2 rounded-full bg-white border border-border text-navy">{h}</span>)}</div>
            </div>
          </div>
        </section>
      )}

      {slug === "recurring-cleaning" && (
        <RecurringPromise variant="section" promise={pricing.recurring_promise} />
      )}

      {/* GREEN NOTE */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Leaf className="w-8 h-8 text-leaf mx-auto mb-4" />
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-navy mb-4">Green Cleaning is always included</h2>
          <p className="text-lg text-muted-foreground">It's part of our standard process — never an upcharge or premium add-on. Learn more about our <Link to="/our-story" className="text-leaf font-medium hover:underline">health-first mission</Link> or explore <Link to="/services" className="text-leaf font-medium hover:underline">all our services</Link>.</p>
        </div>
      </section>

      <OtherLinks currentSlug={slug} />
      {isCommercial ? (
        <section className="py-16 md:py-24 bg-muted/40" data-testid="commercial-quote-cta">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
            <Leaf className="w-8 h-8 text-leaf mx-auto mb-4" />
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy mb-4">Request a Commercial Cleaning Quote</h2>
            <p className="text-lg text-muted-foreground mb-8">Tell us about your facility and we'll send a customized plan and quote.</p>
            <div className="flex justify-center"><RequestQuoteButton testId="commercial-cta-request-quote-btn" /></div>
          </div>
        </section>
      ) : <BookCtaBand heading={`Book Your ${name} Online`} />}
      <FAQ items={faqs} title={`${name} — Frequently Asked Questions`} />
    </div>
  );
}
